import { globalStores, TGlobalStores } from '@/editor/stores';
import { modelManager } from '../manager/entityManager';
import { confirm } from '@/editor/utils/feedback';
import i18n from '@/i18n';
import { EFaceType } from '../entities/type';
import { isRoomType, RegionInfo } from '../data/type';
import { RegionBaseData } from '../data/region/Region';
import { EOperationType } from '../types/operation';
import { EWallFaceLayout } from '../data/face/face';
import { ESeparateWallType } from '../data/separateWall/SeparateWall';
import editorEvent, { EditorEventName } from '../event/editorEvent';

export class RegionOperation {
  private _regionStore: TGlobalStores['regionStore'];
  private _modelStore: TGlobalStores['modelStore'];
  private _faceStore: TGlobalStores['faceStore'];
  private _columnStore: TGlobalStores['structStore'];
  private _gs: TGlobalStores;

  constructor(gs: TGlobalStores) {
    this._gs = gs;
    this._regionStore = gs.regionStore;
    this._modelStore = gs.modelStore;
    this._faceStore = gs.faceStore;
    this._columnStore = gs.structStore;
  }

  private $history_modifyRegionBaseData(regionId: string, newData: Partial<RegionBaseData>) {
    const currentRegion = this._gs.regionStore.getRegionData(regionId);
    if (!currentRegion) return;

    if (!Object.keys(newData).length) return;

    this._gs.history.startOperation();

    this._gs.regionStore.updateRegionBaseData(regionId, newData);

    const roomRegionDatas = this._gs.regionStore.getRoomRegions(currentRegion.relateRoomId);
    if (roomRegionDatas.length === 1 && ('materialInfo' in newData || 'regionInfo' in newData)) {
      if (newData.materialInfo) {
        this._gs.roomStore.updateRoomBaseData(currentRegion.relateRoomId, {
          materialInfo: newData.materialInfo
        });
      }
      if (newData.regionInfo) {
        const { type, name } = newData.regionInfo;
        if (isRoomType(type)) {
          this._gs.roomStore.updateRoomBaseData(currentRegion.relateRoomId, {
            roomInfo: { type, name }
          });
        }
      }
    }

    // 将操作记录压栈
    this._gs.history.completeOperation(EOperationType.RegionModify);
  }

  modifyMaterial(regionId: string, materialId: string) {
    const regionData = this._regionStore.getRegionData(regionId);
    if (!regionData) return;

    this.$history_modifyRegionBaseData(regionId, {
      materialInfo: { id: materialId }
    });

    modelManager.regionManager.updateEntity(regionId);
    return true;
  }

  modifyRegionInfo(regionId: string, _regionInfo: Partial<RegionInfo>) {
    const regionData = this._regionStore.getRegionData(regionId);
    if (!regionData) return;

    const regionInfo = { ...regionData.regionInfo, ..._regionInfo };
    this.$history_modifyRegionBaseData(regionId, { regionInfo });

    modelManager.textInfoManager.updateSingleRegion(regionId);
  }

  private $history_clearRegion(regionId: string) {
    const regionData = this._regionStore.getRegionData(regionId);

    if (!regionData) return;
    this._gs.history.startOperation();

    // 清除分区内家具
    this._modelStore.getFurnitureInPathInfos(regionData.pathInfos, regionData.holeInfos).forEach((furniture) => {
      this._modelStore.removeFurnitureData(furniture.id);
    });

    const defaultFaceMaterial = globalStores.getDefaultMaterial({ type: EFaceType.SepWallFace });
    // 清除分区关联墙面/柱面材质
    this._faceStore
      .getSepWallFaceDatas()
      .filter((faceData) => faceData.relativeInfo.regionId === regionId)
      .forEach((faceData) => {
        if (faceData.relativeInfo.sepType === ESeparateWallType.GlassWall) {
          return;
        }
        if (
          faceData.relativeInfo.sepType === ESeparateWallType.HalfGlassWall &&
          faceData.relativeInfo.layout === EWallFaceLayout.MIDDLE
        ) {
          return;
        }

        globalStores.faceStore.updateFaceMaterial(faceData.id, defaultFaceMaterial);
      });

    this._columnStore.getColumnDatasInPathInfos(regionData.pathInfos, regionData.holeInfos).forEach((columnData) => {
      this._faceStore
        .getColumnFaceDatas()
        .filter((faceData) => faceData.relativeInfo.columnId === columnData.id)
        .forEach((faceData) => {
          globalStores.faceStore.updateFaceMaterial(faceData.id, defaultFaceMaterial);
        });
    });

    modelManager.faceManager.update();

    // 清除分区地板材质
    globalStores.regionStore.updateBaseData(regionId, {
      materialInfo: globalStores.getDefaultMaterial({ type: EFaceType.FloorFace })
    });

    modelManager.regionManager.updateEntity(regionId);

    // 将操作记录压栈
    this._gs.history.completeOperation(EOperationType.ClearArea);

    modelManager.furnitureManager.update();
  }

  async clearRegion(regionId: string) {
    const regionData = this._regionStore.getRegionData(regionId);

    if (!regionData) return;

    const confirmed = await confirm({
      content: i18n.t('draw.clean.confirmRoom')
    });

    if (confirmed) {
      editorEvent.emit(EditorEventName.REPLACE_END);
      this.$history_clearRegion(regionId);
    }
  }
}
